import type { ApplyResult } from '../models/insightAction.types'
import { adaptApplyResult } from '../service/adaptRun'
import { classifyInsightActionError, isCanceled } from '../service/classifyInsightActionError'
import { previewKey, type InsightActionStore } from '../stores/insightActionStore'

import type { InsightActionApiSlice, InsightActionScope } from './insightActionFlow.types'

export interface ApplyActionsFlowDeps {
  api: InsightActionApiSlice<'apply'>
  store: InsightActionStore
}

export class ApplyActionsFlow {
  constructor(private readonly deps: ApplyActionsFlowDeps) {}

  async execute(scope: InsightActionScope): Promise<ApplyResult | null> {
    const store = this.deps.store
    const dataKey = store.preview?.dataKey
    if (!dataKey || store.applying || store.selection.size === 0) return null

    const key = previewKey(scope.accountId, scope.workspaceId, scope.insightId)
    store.setApplying(true)

    try {
      const result = adaptApplyResult(
        await this.deps.api.apply(scope.accountId, scope.workspaceId, scope.insightId, {
          group_key: store.groupKey,
          data_key: dataKey,
          row_keys: [...store.selection],
          idempotency_key: store.idempotencyKey,
        }),
      )

      store.setRun(key, {
        runId: result.runId,
        insightId: scope.insightId,
        status: result.status,
        totalItems: result.totalItems,
        counts: result.counts,
        detail: null,
        items: result.items,
      })
      return result
    } catch (cause) {
      const error = classifyInsightActionError(cause)
      if (isCanceled(error)) return null
      store.setApplyError(error.code)
      return null
    } finally {
      store.setApplying(false)
    }
  }
}
